import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Micro } from '@/components/atoms/micro';
import { Icon, type IconName } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';

export type FormInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  label: ReactNode;
  isRequired?: boolean;
  icon?: IconName;
  suffix?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  className?: string;
  containerClassName?: string;
  /**
   * When true and `type="password"`, renders an eye-icon toggle in the suffix
   * slot that flips the input between "password" and "text". Retained for the
   * login/signup flow until a dedicated PasswordInput molecule lands.
   */
  showPasswordToggle?: boolean;
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(function FormInput(
  {
    id,
    label,
    isRequired,
    icon,
    suffix,
    error,
    hint,
    className,
    containerClassName,
    type = 'text',
    showPasswordToggle,
    ...rest
  },
  ref,
): ReactElement {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const wantsPasswordToggle = type === 'password' && showPasswordToggle === true;
  const resolvedType = wantsPasswordToggle && isPasswordVisible ? 'text' : type;

  const resolvedSuffix: ReactNode = wantsPasswordToggle ? (
    <button
      type="button"
      onClick={() => setPasswordVisible((v) => !v)}
      aria-label="Toggle visibility"
      aria-controls={inputId}
      aria-pressed={isPasswordVisible}
      className={cn(
        'inline-flex items-center justify-center rounded-[var(--r-sm)] h-7 w-7 text-[var(--ink-3)]',
        'hover:text-[var(--ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ink)]',
      )}
    >
      <Icon name={isPasswordVisible ? 'x' : 'eye'} size={14} />
    </button>
  ) : (
    suffix
  );

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      <div className="flex items-center justify-between">
        <Micro as="label" htmlFor={inputId} isRequired={isRequired}>
          {label}
        </Micro>
        {hint && !error && (
          <span id={`${inputId}-hint`} className="text-[10px] text-[var(--ink-3)]">
            {hint}
          </span>
        )}
      </div>
      <div
        data-formfield="wrapper"
        className={cn(
          'flex items-center gap-2 h-11 px-3 bg-[var(--bg-surface)] border rounded-[var(--r-sm)] transition-colors',
          error
            ? 'border-[var(--err)]'
            : 'border-[var(--rule)] focus-within:border-[var(--ink)]',
        )}
      >
        {icon && <Icon name={icon} size={15} className="text-[var(--ink-3)]" />}
        <input
          ref={ref}
          id={inputId}
          type={resolvedType}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={cn(
            'flex-1 bg-transparent text-[14px] text-[var(--ink)] placeholder-[var(--ink-4)] outline-none',
            className,
          )}
          {...rest}
        />
        {resolvedSuffix && (
          <span className="text-[13px] text-[var(--ink-3)] font-mono flex items-center">
            {resolvedSuffix}
          </span>
        )}
      </div>
      {error && (
        <p
          id={`${inputId}-error`}
          className="text-[11px] text-[var(--err)] flex items-center gap-1"
        >
          <Icon name="x" size={11} />
          {error}
        </p>
      )}
    </div>
  );
});
